import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getProjectById,
  handleCreateProject,
  handleUpdateProject,
} from "../../../API/Projects/projectsService";
import { cn } from "../../../lib/utils";
import Header from "../../DashboardNavigation/header";
import Sidebar from "../../DashboardNavigation/sidebar";
import BreadCrumb from "../../breadcrumb";
import { Button } from "../../ui/button";
import { Calendar } from "../../ui/calendar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Heading } from "../../ui/heading";
import { Input } from "../../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Separator } from "../../ui/separator";
import {
  ProjectFormValues,
  projectSchema,
  UpdateProjectFormValues,
  updateProjectSchema,
} from "./project-zod.schema";
import FileUpload from "../../FileUpload/FileUpload";
import QuillEditor from "../../../lib/quill/quill";

export const ProjectForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;
  const router = useNavigate();
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;

  const [loading, setLoading] = useState(false);
  const title = initialData
    ? "Modifier travaux"
    : "Ajouter de nouveaux travaux";
  const description = initialData ? "Modifier travaux" : "";

  const action = initialData ? "Mise à jour" : "Ajouter";

  const form = useForm<ProjectFormValues | UpdateProjectFormValues>({
    resolver: zodResolver(initialData ? updateProjectSchema : projectSchema),
    mode: "onChange",
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateProject(initialData, formData, router);
      } else {
        await handleCreateProject(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const projectData = await getProjectById(initialData);
        setData(projectData);
        form.reset({
          title: projectData.title,
          image: projectData.image
            ? [
                {
                  fileName: projectData.image,
                  name: projectData.image,
                  fileSize: 0,
                  size: 0,
                  key: projectData.image,
                },
              ]
            : [],
          description: projectData.description,
          date_debut: projectData.date_debut
            ? new Date(projectData.date_debut)
            : undefined,
          date_fin: projectData.date_fin
            ? new Date(projectData.date_fin)
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  const breadcrumbItems = [
    { title: "Travaux", link: "/admin-dashboard/travaux" },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/travaux/ajouter",
    },
  ];
  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="gap-8 md:grid md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre </FormLabel>
                      <FormControl>
                        <Input
                          disabled={loading}
                          placeholder="Titre..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="date_debut"
                  render={({ field }) => (
                    <FormItem className="flex flex-col mt-3">
                      <FormLabel>Date Début</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[280px] justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {field.value ? (
                              field.value.toLocaleDateString("fr-FR")
                            ) : (
                              <span>Date début</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date_fin"
                  render={({ field }) => (
                    <FormItem className="flex flex-col mt-3">
                      <FormLabel>Date Fin</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-[280px] justify-start text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {field.value ? (
                              field.value.toLocaleDateString("fr-FR")
                            ) : (
                              <span>Date fin</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <QuillEditor
                        value={(field.value as string) || ""}
                        onChange={(value: string) => {
                          field.onChange(value);
                          form.trigger("description");
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

                {data?.image && (
                  <div className="mt-4 mb-4">
                    <img
                      src={`${uploadUrl}/projects/${data.image}`}
                      alt="Old Image Preview"
                      className="w-10 h-10 h-auto"
                    />
                  </div>
                )}
              <FormField
  control={form.control}
  name="image"
  render={() => (
    <FormItem>
      <FormLabel>Image</FormLabel>
      <FormControl>
        <FileUpload
          onChange={(files) => {
            // Ensure only one file is selected
            const mappedFiles = files.slice(0, 1).map((file) => ({
              key: file.id,
              fileName: file.fileName,
              name: file.fileName,
              fileSize: parseInt(file.fileSize),
              size: parseInt(file.fileSize),
              file: file.file,
            }));
            form.setValue('image', mappedFiles);
          }}
         />
      </FormControl>
      <FormMessage />
    </FormItem>
  )}
/>
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
